import { HttpClient } from '@angular/common/http';
import { Injectable, Type } from '@angular/core';
import { UnitOfWork } from '@data';
import { environment } from '@env';
import { Entity, EntityError } from 'breeze-client';
import * as _ from 'lodash';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import {
  ChangeLogDialogComponent,
  ErrorDialogComponent,
  TextPromptDialogComponent,
  TextPromptDialogData,
  UserFrmDialogComponent,
  ValidationDialogComponent,
} from '../components';
import { EntityFinderDialog, EntityFinderParams } from '../components/base/entity-finder.dialog';
import { OrderIssue, OrderIssuesDialogComponent, OrderIssuesDialogData } from '../components/error/order-issues-dialog.component';
import { SupplierUnavailableDialogComponent, SupplierUnavailableDialogData } from '../components/error/supplier-unavailable-dialog.component';
import { SaveErrorDialogComponent } from '../components/error/save-error-dialog.component';
import {
  EmailConflictDialogComponent,
  EmailConflictDialogResult,
} from '../components/error/email-conflict-dialog.component';
import { ChangeEmailPromptDialogComponent } from '../components/error/change-email-prompt-dialog.component';
import { EmailConflictInfo } from './auth.service';
import { HelpDialogComponent, HelpDialogData } from '../components/help/help-dialog.component';

import { ProximityUser } from '@models';
import { Choice, ChoiceDialogComponent, ChoiceDialogData } from '../components/choice-dialog.component';
import { TextboxDialogComponent, TextboxDialogData } from '../components/textbox-dialog.component';
import { VerifyProceedComponent } from './verify-proceed.component';

// Response type for contacting admins
export interface ContactAdminResponse {
  success: boolean;
  message: string;
  emailsSent: number;
  adminEmails: string[];
}

@Injectable({ providedIn: 'root' })
export class UtilDialogService {
  constructor(
    private confirmationService: ConfirmationService,
    public pngDialogService: DialogService,
    private httpClient: HttpClient,
  ) {}

  // used to create dialogs
  async create<TData>(componentType: Type<any>, data?: TData, config?: DynamicDialogConfig<TData>) {
    const ref = this.pngDialogService.open(componentType, {
      closable: false,
      draggable: true,
      modal: true,
      // baseZIndex: 10000,
      // contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      ...config,
      data: data,
    });
    return lastValueFrom(ref.onClose);
  }

  async createFinder<T extends Entity, TParams extends EntityFinderParams>(
    componentType: Type<EntityFinderDialog<T, TParams>>,
    data?: TParams,
    config?: DynamicDialogConfig<TParams>,
  ) {
    const ref = this.pngDialogService.open(componentType, {
      closable: false,
      draggable: true,
      modal: true,
      // baseZIndex: 10000,
      // contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      ...config,
      data: data,
    });
    const r = await lastValueFrom(ref.onClose);
    return r as T[];
  }

  /*   async createStatusChange<T extends Entity, TParams extends StatusChangeParams >(componentType: Type<StatusChangeDialog<T, TParams>>, data?: TParams, config?: DynamicDialogConfig<TParams>) {
    const ref = this.dialogService.open(componentType, {
      closable: false, draggable: true,
      // baseZIndex: 10000,
      // contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      ...config,
      data: data
    });
    const r = await firstValueFrom(ref.onClose);
    return r as T;
  }
 */

  async confirmUndo(): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: 'Undo Confirmation',
        icon: 'pi pi-exclamation-triangle',
        message:
          'Are you sure you want to undo changes - all of your work since your last save will be lost.  Do you still want to continue?',
        rejectVisible: true,
        accept: () => {
          resolve(true);
        },
        reject: () => {
          resolve(false);
        },
      });
    });
  }

  async confirmCancel(): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: 'Cancel Confirmation',
        icon: 'pi pi-exclamation-triangle',
        message: 'You have unsaved changes.  Do you still want to cancel?',
        rejectVisible: true,
        rejectButtonProps: {
          label: 'No',
          icon: 'pi pi-times',
          variant: 'outlined',
          size: 'small',
        },
        acceptButtonProps: {
          label: 'Yes',
          icon: 'pi pi-check',
          size: 'small',
        },
        accept: () => {
          resolve(true);
        },
        reject: () => {
          resolve(false);
        },
      });
    });
  }

  // async askIfOk(header: string, message: string): Promise<boolean> {
  //   return new Promise((resolve) => {
  //     this.confirmationService.confirm({
  //       header: header,
  //       icon: 'pi pi-exclamation-triangle',
  //       message: message,
  //       acceptLabel: 'Yes',
  //       rejectLabel: 'No',
  //       accept: () => {
  //         resolve(true);
  //       },
  //       reject: () => {
  //         resolve(false);
  //       },
  //     });
  //   });
  // }

  askIfOk(header: string, message: string) {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: header,
        message: message,
        icon: 'pi pi-exclamation-circle',
        rejectVisible: true,
        rejectButtonProps: {
          label: 'No',
          icon: 'pi pi-times',
          variant: 'outlined',
          size: 'small',
        },
        acceptButtonProps: {
          label: 'Yes',
          icon: 'pi pi-check',
          size: 'small',
        },
        accept: () => {
          resolve(true);
        },
        reject: () => {
          resolve(false);
        },
      });
    });
  }

  async okDialog(headerMsg: string, message: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: headerMsg,
        icon: 'pi pi-exclamation-circle',
        message: message,
        rejectVisible: false,
        acceptButtonProps: {
          label: 'Ok',
          icon: 'pi pi-check',
          size: 'small',
        },
        accept: () => {
          resolve(true);
        },
      });
    });
  }

  async warnDialog(headerMsg: string, message: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: headerMsg,
        icon: 'pi pi-exclamation-triangle',
        message: message,
        rejectVisible: false,
        acceptButtonProps: {
          label: 'Ok',
          icon: 'pi pi-check',
          size: 'small',
        },
        accept: () => {
          resolve(true);
        },
      });
    });
  }

  async textboxDialog(headerMsg: string, config: TextboxDialogData): Promise<string> {
    const ref = this.pngDialogService.open(TextboxDialogComponent, {
      data: config,
      header: headerMsg,
      modal: true,
      // style: { 'width': '600px'},
      contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      // baseZIndex: 10000,
      // maximizable: true
    });
    return lastValueFrom(ref.onClose);
  }

  async textpromptDialog(headerMsg: string, config: TextPromptDialogData): Promise<string> {
    const ref = this.pngDialogService.open(TextPromptDialogComponent, {
      data: config,
      header: headerMsg,
      modal: true,
      contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      width: '500px',
    });
    return lastValueFrom(ref.onClose);
  }

  async choiceDialog(headerMsg: string, config: ChoiceDialogData): Promise<Choice> {
    const ref = this.pngDialogService.open(ChoiceDialogComponent, {
      data: config,
      header: headerMsg,
      contentStyle: { overflow: 'auto', height: '300px' },
      modal: true,
      focusOnShow: false,
      width: '500px',
    });
    return lastValueFrom(ref.onClose);
  }

  /**
   * Opens the "Change email to..." prompt dialog. Resolves with the trimmed new-email
   * string when accepted, or <code>undefined</code> when the user cancels.
   */
  async changeEmailPromptDialog(currentDisplay: string): Promise<string | undefined> {
    return new Promise((resolve) => {
      const ref = this.pngDialogService.open(ChangeEmailPromptDialogComponent, {
        data: { currentDisplay },
        header: 'Change Email',
        modal: true,
        style: { width: '460px' },
        contentStyle: { overflow: 'auto' },
        focusOnShow: true,
      });
      ref.onClose.subscribe((result: string | undefined) => {
        resolve(result);
      });
    });
  }

  /**
   * Opens the email-conflict resolution dialog. Resolves to <code>'reassigned'</code>
   * when the caller successfully moved the email and the form can retry the save, or
   * <code>'cancelled'</code> when the caller declined and the form should leave the
   * field-level validation error in place.
   */
  async emailConflictDialog(conflict: EmailConflictInfo, toUserId: string): Promise<EmailConflictDialogResult> {
    let ref: DynamicDialogRef;
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(EmailConflictDialogComponent, {
        data: { conflict, toUserId },
        header: 'Email Already In Use',
        modal: true,
        styleClass: 'red-header',
        style: { width: '560px' },
        contentStyle: { overflow: 'auto' },
        focusOnShow: false,
      });
      ref.onClose.subscribe((result: EmailConflictDialogResult) => {
        resolve(result ?? 'cancelled');
      });
    });
  }

  async saveErrorDialog(err: Error): Promise<boolean> {
    let ref: DynamicDialogRef;
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(SaveErrorDialogComponent, {
        data: {
          error: err,
        },
        header: 'Save Error',
        modal: true,
        styleClass: 'red-header',
        style: { width: '600px' },
        contentStyle: { overflow: 'auto' },
        focusOnShow: false,
        // baseZIndex: 10000,
        // maximizable: true
      });

      ref.onClose.subscribe(() => {
        resolve(true);
      });
    });
  }

  async errorDialog(err: Error, header?: string, canReload?: boolean): Promise<boolean> {
    let ref: DynamicDialogRef;
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(ErrorDialogComponent, {
        data: {
          error: err,
          canReload: canReload,
        },
        header: header ?? err.name ?? 'Error',
        modal: true,
        styleClass: 'red-header',
        style: { width: '600px' },
        contentStyle: { overflow: 'auto' },
        focusOnShow: false,
        // baseZIndex: 10000,
        // maximizable: true
      });

      ref.onClose.subscribe(() => {
        resolve(true);
      });
    });
  }

  async statusErrorDialog(errs: string[]): Promise<boolean> {
    let errString = '';
    errs.forEach((s) => {
      errString += s + '<br/>';
    });
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: 'Status Change Error',
        icon: 'pi pi-exclamation-triangle',
        message: errString,
        acceptLabel: 'Ok',
        rejectVisible: false,
        accept: () => {
          resolve(true);
        },
      });
    });
  }

  async validationDialog(
    parent: any,
    errEntities: Entity[],
    callback: (ee: EntityError, event: MouseEvent) => void,
    showEntityTypes: boolean = false,
    groupRepeated: boolean = false,
  ): Promise<boolean> {
    let ref: DynamicDialogRef;
    const ht = errEntities.length > 10 ? '500px' : '100%';
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(ValidationDialogComponent, {
        data: {
          parent: parent,
          errEntities,
          callback,
          showEntityTypes,
          groupRepeated,
        },
        header: 'Validation Errors',
        styleClass: 'yellow-header',
        width: '800px',
        contentStyle: { height: ht },
        focusOnShow: false,
        draggable: true,
        modal: false,
        resizable: true,
      });
      // ref is null if called while previous dialog is still up.
      ref?.onClose.subscribe(() => {
        resolve(true);
      });
    });
  }

  async verifyProceedDialog(header: string, message: string): Promise<boolean> {
    let ref: DynamicDialogRef;
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(VerifyProceedComponent, {
        data: {
          message: message,
        },
        header: header,
        width: '800px',
        closable: false,
        draggable: false,
        focusOnShow: false,
        modal: true,
        resizable: false,
      });
      // ref is null if called while previous dialog is still up.
      ref?.onClose.subscribe((data) => {
        resolve(data);
      });
    });
  }

  async helpDialog(
    title: string,
    parent: any,
    field: string | undefined,
    callback?: (ee: EntityError, event: MouseEvent) => void,
    uow?: UnitOfWork,
    anchor?: string,
  ): Promise<boolean> {
    let ref: DynamicDialogRef;
    title = 'Help: ' + (title || this.getTitle(parent));
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(HelpDialogComponent, {
        data: {
          parent,
          field,
          callback,
          uow,
          anchor,
        } as HelpDialogData,
        header: title,
        width: '800px',
        styleClass: 'goldenrod',
        contentStyle: { height: '70vh' },
        focusOnShow: false,
        draggable: true,
        modal: false,
        resizable: true,
      });
      // ref is null if called while previous dialog is still up.
      ref?.onClose.subscribe(() => {
        resolve(true);
      });
    });
  }

  private getTitle(component: any) {
    return component.title || _.startCase(component.constructor.name.replace(/(Frm)?Component$/, ''));
  }

  /** `accountId` asks for a whole account's history and overrides `entity`/`entityId`. */
  async changeLogDialog(uow: UnitOfWork, filterModel: any, entity?: Entity, entityId?: string, accountId?: string): Promise<boolean> {
    let ref: DynamicDialogRef;
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(ChangeLogDialogComponent, {
        data: {
          uow,
          filterModel,
          entity,
          entityId,
          accountId,
        },
        header: 'Change Log',
        styleClass: 'yellow-header',
        width: '70vw',
        contentStyle: { height: '70vh' },
        focusOnShow: false,
        draggable: true,
        modal: true,
        resizable: true,
      });
      // ref is null if called while previous dialog is still up.
      ref?.onClose.subscribe(() => {
        resolve(true);
      });
    });
  }

  async userFrmDialog(proximityUser: ProximityUser, headerMsg: string, updateUrl?: string, canRequestEdit = false) {
    const ref = this.pngDialogService.open(UserFrmDialogComponent, {
      ...UserFrmDialogComponent.config,
      data: { user: proximityUser, updateUrl, canRequestEdit },
      header: headerMsg,
    });
    return lastValueFrom(ref.onClose);
  }

  async orderIssuesDialog(header: string, issues: OrderIssue[], accountUserId?: string): Promise<boolean> {
    let ref: DynamicDialogRef;
    return new Promise((resolve) => {
      ref = this.pngDialogService.open(OrderIssuesDialogComponent, {
        data: {
          issues: issues,
          accountUserId: accountUserId,
          dialogService: this,
        } as OrderIssuesDialogData,
        header: header,
        modal: true,
        style: { width: '600px' },
        contentStyle: { overflow: 'auto' },
        focusOnShow: false,
        closable: false,
      });

      ref.onClose.subscribe(() => {
        resolve(true);
      });
    });
  }

  /** Send an email to Account Administrators about order issues */
  async contactAdminsAboutOrderIssue(accountUserId: string, issues: OrderIssue[]): Promise<ContactAdminResponse> {
    const url = environment.apiUrl + 'Communication/ContactAdminsAboutOrderIssue';
    const request = {
      accountUserId: accountUserId,
      issues: issues.map((i) => ({
        message: i.message,
        details: i.details,
      })),
    };
    return firstValueFrom(this.httpClient.post<ContactAdminResponse>(url, request));
  }

  /** Send a profile edit request to Account Administrators */
  async requestProfileEdit(accountUserId: string, message: string): Promise<ContactAdminResponse> {
    const url = environment.apiUrl + 'Communication/RequestProfileEdit';
    return firstValueFrom(this.httpClient.post<ContactAdminResponse>(url, { accountUserId, message }));
  }

  /** Email + in-app-notify the Supplier Administrator who manages this account's relationship
   * with a supplier (or the supplier's primary admin as fallback) that a user could not
   * place an order because the Supplier or SupplierAccount is on Hold. */
  async contactSupplierAdminAboutBlockedItem(accountUserId: string, supplierId: string, reason: string): Promise<ContactAdminResponse> {
    const url = environment.apiUrl + 'Communication/ContactSupplierAdminAboutBlockedItem';
    return firstValueFrom(this.httpClient.post<ContactAdminResponse>(url, { accountUserId, supplierId, reason }));
  }

  /** Open the "Supplier Unavailable" dialog with a 'Contact Administrator' button. The dialog
   *  shows `message` to the user and, on click, emails `emailReason` to the supplier administrator
   *  assigned to this (account, supplier) pair via ContactSupplierAdminAboutBlockedItem. */
  async supplierUnavailableDialog(message: string, accountUserId: string, supplierId: string, emailReason: string): Promise<void> {
    return new Promise((resolve) => {
      const ref = this.pngDialogService.open(SupplierUnavailableDialogComponent, {
        data: {
          message,
          accountUserId,
          supplierId,
          emailReason,
          dialogService: this,
        } as SupplierUnavailableDialogData,
        header: 'Supplier Unavailable',
        modal: true,
        style: { width: '560px' },
        contentStyle: { overflow: 'auto' },
        focusOnShow: false,
        closable: false,
      });
      ref.onClose.subscribe(() => resolve());
    });
  }
}
